// src/context/progressStore.js
//
// REESCRITO — ya no simula progreso en memoria por courseId+contentId.
// El backend real modela el progreso por matriculaId (matrícula de
// curso) + contenidoId, con endpoints dedicados para quiz/examen
// (evaluacionService) y entregas de actividad (entregaService).
//
// CAMBIO DE CONTRATO IMPORTANTE:
//   - Todas las acciones ahora son ASYNC.
//   - Las funciones que antes recibían `courseId` ahora reciben
//     `matriculaId` (el id de la matrícula del estudiante en ESE
//     curso, no el id del curso). Se obtiene desde meService.getMisCursos()
//     (campo `enrollmentId`) o desde aprendizajeService.matricular().
//   - Los quizzes ya no se resuelven completos en el cliente — el flujo
//     real es: iniciarIntento() → responder → responderIntento() →
//     el backend califica y devuelve el resultado.

import { create } from 'zustand';
import { aprendizajeService, evaluacionService, entregaService } from '../services/api';

const useProgressStore = create((set, get) => ({
  // Progreso de la matrícula actualmente cargada (una a la vez, la que
  // se está viendo en CourseView). Estructura: { [contentId]: {...} }
  currentEnrollmentId: null,
  progress: {},           // { [contentId]: ProgresoContenidoDto-like }
  courseProgress: 0,       // % global de la matrícula actual
  loading: false,
  error: null,

  // ── Cargar el progreso completo de una matrícula ────────────────────
  fetchProgress: async (matriculaId) => {
    set({ loading: true, error: null });
    try {
      const data = await aprendizajeService.getProgreso(matriculaId);
      if (!data) { set({ loading: false }); return null; }
      const byContentId = {};
      (data.contents || []).forEach((c) => { byContentId[c.contentId] = c; });
      set({
        currentEnrollmentId: matriculaId,
        progress: byContentId,
        courseProgress: data.progress,
        loading: false,
      });
      return data;
    } catch (err) {
      set({ loading: false, error: err.message });
      return null;
    }
  },

  // ── Matricularse a un curso ─────────────────────────────────────────
  enrollCourse: async (courseId, matriculaInstId = null) => {
    try {
      const result = await aprendizajeService.matricular(courseId, matriculaInstId);
      return { success: true, enrollmentId: result.matriculaId };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Lectura de progreso de un contenido (síncrona, sobre el caché) ──
  getContentProgress: (contentId) => get().progress[contentId] || null,

  isContentCompleted: (contentId) => !!get().progress[contentId]?.completed,

  // Estado del contenido para el estudiante actual.
  // Valores: 'Bloqueado' | 'Disponible' | 'EnProgreso' | 'Completado'.
  // Si no hay progreso cargado, asume Bloqueado (fail-safe).
  getContentStatus: (contentId) =>
    get().progress[contentId]?.status || 'Bloqueado',

  isContentUnlocked: (contentId) => {
    const st = get().progress[contentId]?.status;
    return st === 'Disponible' || st === 'EnProgreso' || st === 'Completado';
  },

  getProgressId: (contentId) => get().progress[contentId]?.progressId || null,
  getAttemptsMade: (contentId) => get().progress[contentId]?.attemptsMade || 0,
  getScore: (contentId) => get().progress[contentId]?.scoreObtained,

  getCourseProgress: () => get().courseProgress,

  // ── Materiales / contenido simple (marcar como leído) ───────────────
  markMaterialRead: async (matriculaId, contentId) => {
    try {
      await aprendizajeService.marcarCompletado(matriculaId, contentId);
      await get().fetchProgress(matriculaId);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Videos ───────────────────────────────────────────────────────────
  updateVideoProgress: async (matriculaId, contentId, watchedPercent) => {
    try {
      const result = await aprendizajeService.actualizarVideoProgreso(matriculaId, contentId, watchedPercent);
      set((state) => ({
        progress: {
          ...state.progress,
          [contentId]: {
            ...state.progress[contentId],
            videoPercent: result.porcentaje,
            completed: result.completado,
          },
        },
      }));
      return { success: true, completed: result.completado };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Quizzes / exámenes ──────────────────────────────────────────────
  // Flujo real: iniciar el intento → responder TODO el quiz de una vez
  // (el backend califica y devuelve el detalle pregunta por pregunta).
  iniciarIntentoQuiz: async (matriculaId, contentId) => {
    try {
      const result = await evaluacionService.iniciarIntento(matriculaId, contentId);
      return { success: true, attemptId: result.intentoId, attemptNumber: result.numeroIntento };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  submitQuizAttempt: async (matriculaId, attemptId, respuestas) => {
    try {
      const resultado = await evaluacionService.responderIntento(attemptId, respuestas);
      await get().fetchProgress(matriculaId);
      return { success: true, ...resultado };
    } catch (err) {
      return { success: false, message: err.message };
    }
  },

  getQuizPreguntas: async (contentId) => {
    try {
      return await evaluacionService.getQuizParaEstudiante(contentId);
    } catch {
      return [];
    }
  },

  // ── Actividades (entregas de archivos) ──────────────────────────────
  submitActivity: async (progresoId, comentario, archivos, matriculaId) => {
    try {
      const entrega = await entregaService.create(progresoId, comentario, archivos);
      if (matriculaId) await get().fetchProgress(matriculaId);
      return { success: true, entregaId: entrega.id };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  subirArchivoEntrega: async (entregaId, file) => {
    try {
      return await entregaService.subirArchivo(entregaId, file);
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Instructor: pendientes de calificar + calificación ──────────────
  getPendientesCalificar: async (courseId = null) => {
    try {
      return await entregaService.getPendientesInstructor(courseId);
    } catch {
      return [];
    }
  },

  gradeActivity: async (entregaId, score, feedback, rubrica = null) => {
    try {
      await entregaService.calificar(entregaId, score, feedback, rubrica);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  },

  // ── Instructor: estudiantes matriculados en un curso ─────────────────
  getStudentsInCourse: async (courseId, params = {}) => {
    try {
      return await aprendizajeService.getEstudiantesCurso(courseId, params);
    } catch {
      return { data: [], total: 0 };
    }
  },

  // ── Estadísticas del usuario actual ──────────────────────────────────
  getStats: () => {
    const { progress } = get();
    const values = Object.values(progress);
    const completed = values.filter((c) => c.completed).length;
    const withScore = values.filter((c) => c.scoreObtained != null);
    const avgScore = withScore.length > 0
      ? Math.round(withScore.reduce((s, c) => s + c.scoreObtained, 0) / withScore.length)
      : 0;
    return {
      contentsCompleted: completed,
      totalContents: values.length,
      averageScore: avgScore,
    };
  },
}));

export default useProgressStore;
